// Arabic -> Latin reading aid in the style of the user's AMAU lesson notes (ā ī ū, ʿ ʾ, ḥ ṭ ṣ ḍ ẓ,
// th dh kh gh sh, full case endings, "al-" with sun letters assimilated: as-sarīri).
// Order of preference, applied by readingFor(): the user's own transliteration from their notes,
// then a rule-based reading of VOWELLED Arabic. Unvowelled Arabic cannot be read reliably, so it
// gets no reading rather than a wrong one. Pure module (no imports) so it can be unit-tested.

const CONSONANTS:Record<string,string>={
 'ء':'ʾ','أ':'ʾ','إ':'ʾ','ؤ':'ʾ','ئ':'ʾ','ب':'b','ت':'t','ث':'th','ج':'j','ح':'ḥ','خ':'kh','د':'d','ذ':'dh',
 'ر':'r','ز':'z','س':'s','ش':'sh','ص':'ṣ','ض':'ḍ','ط':'ṭ','ظ':'ẓ','ع':'ʿ','غ':'gh','ف':'f','ق':'q','ك':'k',
 'ل':'l','م':'m','ن':'n','ه':'h','و':'w','ي':'y','ى':'y','ة':'h','ا':'ʾ','ٱ':'ʾ','آ':'ʾ',
}
const SUN=new Set(['ت','ث','د','ذ','ر','ز','س','ش','ص','ض','ط','ظ','ل','ن'])
const FATHA='َ',DAMMA='ُ',KASRA='ِ',FATHATAN='ً',DAMMATAN='ٌ',KASRATAN='ٍ',SHADDA='ّ',SUKUN='ْ',DAGGER='ٰ'
const MARK=/[ً-ْٰ]/
const LETTER=/[ء-يٱ]/
const DROP=/[ـؐ-ؚۖ-ۭٓ-ٟ]/g // tatweel, Qur'anic annotation marks, maddah above (handled via آ)
const PUNCT:Record<string,string>={'،':',','؛':';','؟':'?','٪':'%','«':'"','»':'"'}

export const hasArabic=(text:string)=>/[؀-ۿ]/.test(text||'')

function prepare(text:string){return (text||'').normalize('NFC').replace(/آ/g,'أَا').replace(DROP,'').replace(/[٠-٩]/g,d=>String(d.charCodeAt(0)-0x0660))}

// Enough vowel marks to read: at least one mark per three letters.
export function isVowelled(text:string){const t=prepare(text);const letters=(t.match(/[ء-يٱ]/g)||[]).length,marks=(t.match(/[ً-ْٰ]/g)||[]).length;return letters>0&&marks>0&&marks*3>=letters}

type Unit={ch:string;marks:string}
function units(word:string):Unit[]{const out:Unit[]=[];for(const ch of word){if(MARK.test(ch)&&out.length)out[out.length-1].marks+=ch;else out.push({ch,marks:''})}return out}
const vowelOf=(marks:string)=>marks.includes(FATHATAN)?'an':marks.includes(DAMMATAN)?'un':marks.includes(KASRATAN)?'in':marks.includes(FATHA)?'a':marks.includes(DAMMA)?'u':marks.includes(KASRA)?'i':''
const skeleton=(word:string)=>word.replace(/[ً-ْٰ]/g,'')

const FIXED:Record<string,string>={'هذا':'hādhā','هذه':'hādhihi','هذان':'hādhāni','هذين':'hādhayni','ذلك':'dhālika','ذلكم':'dhālikum','هؤلاء':'hāʾulāʾi','أولئك':'ulāʾika','لكن':'lākin','الذي':'alladhī','التي':'allatī','الذين':'alladhīna','اللذان':'alladhāni','اللتان':'allatāni','اللاتي':'allātī','الرحمن':'ar-raḥmān'}

function readWord(word:string):string{
 // Allāh with its prefixes: الله، والله، بالله، لله، ولله
 const god=skeleton(word).match(/^([وف]?)([ب]?)(ا?)لله$/)
 if(god){const pre=(god[1]==='و'?'wa-':god[1]==='ف'?'fa-':''),ending=vowelOf(units(word).at(-1)?.marks||'').replace(/n$/,'');return pre+(god[2]?'billāh':god[3]?'Allāh':'lillāh')+ending}
 // Words whose long ā is not written in ordinary script (only as a dagger alif, if at all).
 const fixed=skeleton(word).match(/^([وف]?)(.+)$/)
 for(const w of fixed?[fixed[0],fixed[2]]:[]){const r=FIXED[w];if(r)return (w===fixed![0]?'':fixed![1]==='و'?'wa-':'fa-')+r}
 const u=units(word)
 let out='',i=0
 // Joined prefixes: wa/fa, then bi/ka (+ article), or li + article (لِلْ, its alif dropped).
 const lamOfArticle=(k:number)=>u[k]?.ch==='ل'&&!vowelOf(u[k].marks)&&u.length>k+1
 const isArticle=(k:number)=>(u[k]?.ch==='ا'||u[k]?.ch==='ٱ')&&!vowelOf(u[k].marks)&&lamOfArticle(k+1)
 const isLil=(k:number)=>u[k]?.ch==='ل'&&u[k].marks.includes(KASRA)&&lamOfArticle(k+1)
 const article=(lam:number,prefixed:boolean)=>{const next=u[lam+1];if(next&&SUN.has(next.ch)&&(next.marks.includes(SHADDA)||!u[lam].marks.includes(SUKUN))){next.marks=next.marks.replace(SHADDA,'');return (prefixed?'':'a')+CONSONANTS[next.ch]+'-'}return prefixed?'l-':'al-'}
 const joined=(k:number)=>isArticle(k+1)||isLil(k)||'بك'.includes(u[k]?.ch||'')&&!!vowelOf(u[k].marks)&&isArticle(k+1)
 const isBareAlif=(k:number)=>(u[k]?.ch==='ا'||u[k]?.ch==='ٱ')&&!vowelOf(u[k].marks)
 let stem=0
 if(u.length>3&&'وف'.includes(u[0].ch)&&u[0].marks.includes(FATHA)&&(isArticle(1)||joined(1))){out=CONSONANTS[u[0].ch]+'a-';stem=1}
 if(isLil(stem)){out+='li-'+article(stem+1,true);stem+=2}
 else if('بك'.includes(u[stem]?.ch||'')&&vowelOf(u[stem].marks)&&isArticle(stem+1)){out+=CONSONANTS[u[stem].ch]+vowelOf(u[stem].marks)+'-'+article(stem+2,true);stem+=3}
 else if(isArticle(stem)){out+=article(stem+1,stem>0);stem+=2}
 else if(stem===0&&isBareAlif(0)&&u[1]?.ch==='ل'&&u[1].marks.includes(KASRA)&&isBareAlif(2)){out='al-i';stem=3} // الِاسْمُ: al-ism
 else if(stem===0&&u.length>3&&'وف'.includes(u[0].ch)&&u[0].marks.includes(FATHA)&&isBareAlif(1)&&u[2]?.marks.includes(SUKUN)){out=CONSONANTS[u[0].ch]+'a-';stem=2} // وَاسْتَعِنْ: wa-staʿin
 i=stem
 let last='' // last vowel written: a / i / u / '' (none)
 for(;i<u.length;i++){
  const {ch,marks}=u[i],v=vowelOf(marks),first=i===stem
  if(first&&(ch==='ا'||ch==='ٱ')&&!v&&(i>0||u[i+1]?.marks.includes(SUKUN))){out+='i';last='i';continue} // hamzat al-waṣl: al-ismiyyah, ihdinā, istighrāq
  if(PUNCT[ch]!==undefined){out+=PUNCT[ch];last='';continue}
  if(!LETTER.test(ch)){out+=ch;last='';continue}
  if(ch==='ا'||ch==='ٱ'){
   if(first){out+=v.replace(/^(a|i|u)n$/,'$1');last=v;continue} // initial alif carries only its vowel
   if(last==='an'||out.endsWith('an')&&!v)continue // alif after tanwīn fatḥ is silent
   if(last==='ū'&&i===u.length-1&&!v&&u[i-1]?.ch==='و'){continue} // wāw al-jamāʿah: kataboo -> katabū
   if(!v){out=last==='a'?out.slice(0,-1)+'ā':out+'ā';last='ā';continue}
  }
  if(ch==='ى'){if(marks.includes(FATHATAN)){out=out.endsWith('a')?out.slice(0,-1)+'an':out+'an';last='an';continue}out=last==='a'?out.slice(0,-1)+'ā':out+'ā';last='ā';continue}
  if((ch==='و'&&last==='u'||ch==='ي'&&last==='i')&&!v&&!marks.includes(SHADDA)){out=out.slice(0,-1)+(ch==='و'?'ū':'ī');last=ch==='و'?'ū':'ī';continue}
  if(ch==='ة'){if(v){out+='t'+v;last=v}else{out+='h';last=''}continue}
  if(first&&'أإ'.includes(ch)){const vv=v||(ch==='إ'?'i':'a');out+=vv.replace(/n$/,'');last=vv;continue}
  let c=CONSONANTS[ch]||ch
  if(marks.includes(SHADDA))c+=c
  out+=c
  if(marks.includes(DAGGER)){out+='ā';last='ā';continue}
  out+=v;last=v
 }
 return out.replace(/iyy$/,'ī').replace(/uww$/,'ū')
}

export function readArabic(text:string):string{
 const t=prepare(text).trim()
 if(!t||!isVowelled(t))return''
 const words=t.split(/(\s+)/).map(w=>/\s/.test(w)?' ':readWord(w)).join('').replace(/\s+/g,' ').replace(/ ([,;?.!])/g,'$1').trim()
 return capitalise(words)
}

function capitalise(s:string){const m=s.match(/^([ʿʾ"(]*)(.)/);return m?m[1]+m[2].toUpperCase()+s.slice(m[0].length):s}

// Root letters as Lane writes them (unvowelled), shown spaced: كتب -> k–t–b.
export function readRoot(root:string){const letters=Array.from(prepare(root).replace(/[ً-ْٰ\s]/g,'')).filter(ch=>LETTER.test(ch));if(!letters.length)return'';return letters.map(ch=>ch==='ا'?'ʾ':ch==='ى'?'y':CONSONANTS[ch]||ch).join('–')}

// The user's own transliterations, from the Arabic course items extracted from their notes.
export type NotesIndex={exact:Map<string,string>;bare:Map<string,string|null>}
const noteKey=(arabic:string)=>prepare(arabic).replace(/[.!?؟،,:;]+$/,'').replace(/\s+/g,' ').trim()
const cleanNoteReading=(t:string)=>t.replace(/^\s*(?:vocabulary|example|plural|singular|masculine|feminine|word|phrase|sentence)\s*:\s*/i,'').trim()
export function buildNotesIndex(items:{arabic:string;transliteration:string}[]):NotesIndex{
 const exact=new Map<string,string>(),bare=new Map<string,string|null>()
 for(const item of items){
  const reading=cleanNoteReading(item.transliteration||'');if(!item.arabic||!reading||hasArabic(reading))continue
  const key=noteKey(item.arabic);if(!key)continue
  if(!exact.has(key))exact.set(key,reading)
  const b=skeleton(key);const seen=bare.get(b);bare.set(b,seen===undefined||seen===reading?reading:null) // null = ambiguous
 }
 return {exact,bare}
}

// given: a transliteration already attached to this Arabic (flashcard, course item) — always wins.
export function readingFor(arabic:string,index?:NotesIndex|null,given?:string|null):string{
 const own=(given||'').trim();if(own)return own
 if(!hasArabic(arabic))return''
 const key=noteKey(arabic)
 const fromNotes=index?.exact.get(key)||index?.bare.get(skeleton(key))
 if(fromNotes)return fromNotes
 return readArabic(arabic)
}
